import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import Logo from '../components/Logo'
import { skillCreatorChat, createSkill, listAllProjects } from '../api'

// ─── Types ───────────────────────────────────────────────────────────────────

interface Message {
  role: 'assistant' | 'user'
  content: string
  chips?: string[]
}

type Step = 'purpose' | 'users' | 'platforms' | 'guardrails' | 'generate'

const STEPS: { key: Step; label: string }[] = [
  { key: 'purpose',    label: 'Purpose' },
  { key: 'users',      label: 'Users' },
  { key: 'platforms',  label: 'Platforms' },
  { key: 'guardrails', label: 'Guardrails' },
  { key: 'generate',   label: 'Generate' },
]

// Map message count → step progress
function inferStep(userMsgCount: number): Step {
  if (userMsgCount === 0) return 'purpose'
  if (userMsgCount === 1) return 'users'
  if (userMsgCount === 2) return 'platforms'
  if (userMsgCount === 3) return 'guardrails'
  return 'generate'
}

function sessionId() {
  return 'sess_' + Math.random().toString(36).slice(2, 11)
}

// Simple slug from skill name
function toSlug(name: string) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

function parseMeta(content: string) {
  const get = (key: string) => {
    const m = content.match(new RegExp(`^${key}:\\s*(.*)$`, 'm'))
    return m ? m[1].trim() : ''
  }
  return {
    name: get('name') || 'untitled',
    version: get('version') || 'v1.0',
    status: get('status') || 'draft',
    owner: get('owner') || '',
    llm: get('llm') || 'claude',
    tags: get('tags') || '',
  }
}

// Strip YAML frontmatter for rendered preview
function stripFrontmatter(md: string) {
  if (md.startsWith('---')) {
    const end = md.indexOf('\n---', 3)
    if (end !== -1) return md.slice(end + 4).trimStart()
  }
  return md
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function SkillCreatorPage() {
  const navigate = useNavigate()
  const [sid] = useState(sessionId)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [typing, setTyping] = useState(false)
  const [skillReady, setSkillReady] = useState(false)
  const [skillContent, setSkillContent] = useState<string | null>(null)
  const [skillSlug, setSkillSlug] = useState<string | null>(null)
  const [messagesRemaining, setMessagesRemaining] = useState(10)
  const [copied, setCopied] = useState(false)
  const [currentStep, setCurrentStep] = useState<Step>('purpose')
  const [creatingSkill, setCreatingSkill] = useState(false)
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([])
  const [selectedProjectId, setSelectedProjectId] = useState<string>('')

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // ── Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, typing])

  // ── Bootstrap: send initial greeting from assistant + load projects
  useEffect(() => {
    startConversation()
    listAllProjects().then(r => {
      const groups: any[] = Array.isArray(r.data) ? r.data : []
      setProjects(groups.flatMap(g => g.projects || []).map((p: any) => ({ id: p.id, name: p.name })))
    }).catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const startConversation = async () => {
    setTyping(true)
    try {
      const res = await skillCreatorChat([], sid)
      const { reply, chips } = res.data
      setMessages([{ role: 'assistant', content: reply, chips }])
    } catch {
      setMessages([{
        role: 'assistant',
        content: "Hi! I'll help you build a well-structured skill. What would you like this skill to help with?",
        chips: ['Writing', 'Product management', 'Sales', 'Customer support', 'Engineering', 'Research', 'Personal productivity'],
      }])
    } finally {
      setTyping(false)
    }
  }

  const sendMessage = async (text?: string) => {
    const content = (text ?? input).trim()
    if (!content || typing) return

    const userMsg: Message = { role: 'user', content }
    const newMessages = [...messages, userMsg]
    setMessages(newMessages)
    setInput('')
    setTyping(true)

    const userCount = newMessages.filter(m => m.role === 'user').length
    setCurrentStep(inferStep(userCount))

    try {
      const apiMessages = newMessages.map(m => ({ role: m.role, content: m.content }))
      const res = await skillCreatorChat(apiMessages, sid)
      const { reply, chips, skill_ready, skill_content, messages_remaining } = res.data

      setMessagesRemaining(messages_remaining)

      if (skill_ready && skill_content) {
        // Don't show the raw markdown reply — show a clean success message
        const meta = parseMeta(skill_content)
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Your skill "${meta.name || 'new skill'}" is ready! Check the preview on the right.`,
          chips: [],
        }])
        setSkillReady(true)
        setSkillContent(skill_content)
        setCurrentStep('generate')
        await autoCreateSkill(skill_content, selectedProjectId || null)
      } else {
        const assistantMsg: Message = { role: 'assistant', content: reply, chips }
        setMessages(prev => [...prev, assistantMsg])
      }
    } catch (err: any) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Something went wrong. Please try again.',
        chips: [],
      }])
    } finally {
      setTyping(false)
    }
  }

  const autoCreateSkill = async (content: string, projectId: string | null) => {
    setCreatingSkill(true)
    const meta = parseMeta(content)
    const slug = toSlug(meta.name)
    try {
      const res = await createSkill({
        name: meta.name,
        description: meta.name,
        content,
        status: 'draft',
        tags: [],
        ...(projectId ? { project_id: projectId } : {}),
      })
      setSkillSlug(res.data?.id || slug)
    } catch {
      // Skill may already exist — still allow opening editor
      setSkillSlug(slug)
    } finally {
      setCreatingSkill(false)
    }
  }

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const copyMarkdown = () => {
    if (!skillContent) return
    navigator.clipboard.writeText(skillContent)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const userMsgCount = messages.filter(m => m.role === 'user').length
  const showRemaining = userMsgCount >= 3 && !skillReady
  const meta = skillContent ? parseMeta(skillContent) : null

  return (
    <div className="flex flex-col h-screen bg-[#f5f5f4] dark:bg-[#111] overflow-hidden">

      {/* ── Nav ── */}
      <nav className="h-[52px] bg-white dark:bg-[#1e1e1e] border-b border-[#e5e5e3] dark:border-[#2e2e2e] flex items-center px-5 gap-3 flex-shrink-0">
        <Logo linkTo="/" />
        <div className="w-px h-[18px] bg-[#e5e5e3] dark:bg-[#2e2e2e]" />
        <span className="text-[14px] text-[#64748b] dark:text-[#666]">New skill</span>
        {projects.length > 0 && (
          <>
            <div className="w-px h-[18px] bg-[#e5e5e3] dark:bg-[#2e2e2e]" />
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-medium uppercase tracking-[0.5px] text-[#64748b] dark:text-[#666]">Project</span>
              <select
                value={selectedProjectId}
                onChange={e => setSelectedProjectId(e.target.value)}
                className="text-[12px] rounded-[6px] px-2 py-[5px] outline-none cursor-pointer"
                style={{ border: '1px solid var(--border, #e5e5e3)', background: 'var(--surface2, #fff)', color: 'var(--text1, #1a1a1a)' }}
              >
                <option value="">Unassigned</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => navigate('/')}
            className="px-3 py-1.5 border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[6px] text-[13px] font-medium text-[#1a1a1a] dark:text-[#f0f0ef] bg-transparent hover:bg-[#f5f5f4] dark:hover:bg-[#252525] cursor-pointer"
          >
            Cancel
          </button>
          <button
            disabled={!skillReady || !skillSlug || creatingSkill}
            onClick={() => skillSlug && navigate(`/skills/${skillSlug}`)}
            className="px-3 py-1.5 bg-[#1a1a1a] dark:bg-[#f0f0ef] text-white dark:text-[#1a1a1a] rounded-[6px] text-[13px] font-medium hover:bg-[#333] dark:hover:bg-[#ddd] disabled:opacity-40 disabled:cursor-default cursor-pointer"
          >
            {creatingSkill ? 'Saving…' : 'Open in editor →'}
          </button>
        </div>
      </nav>

      {/* ── Split ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Left: Chat ── */}
        <div className="w-[420px] min-w-[420px] border-r border-[#e5e5e3] dark:border-[#2e2e2e] flex flex-col bg-white dark:bg-[#1e1e1e]">

          {/* Chat header */}
          <div className="px-4 py-3.5 border-b border-[#e5e5e3] dark:border-[#2e2e2e]">
            <div className="text-[13px] font-medium text-[#1a1a1a] dark:text-[#f0f0ef] mb-0.5">Skill creation assistant</div>
            <div className="text-[11px] text-[#64748b] dark:text-[#666]">Answer a few questions to generate your skill</div>
            <div className="inline-flex items-center gap-1.5 mt-1.5 px-[7px] py-[2px] rounded-full bg-[#f5f0ff] dark:bg-[#1e1530]">
              <div className="w-[5px] h-[5px] rounded-full bg-[#7c3aed]" />
              <span className="text-[10px] font-medium text-[#7c3aed]">Claude Haiku</span>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3">
            {messages.map((msg, i) => (
              <div key={i} className="flex flex-col gap-1.5">
                <div className={`flex gap-2 items-end ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  {/* Avatar */}
                  <div className={`w-[26px] h-[26px] rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 ${
                    msg.role === 'assistant'
                      ? 'bg-[#f5f0ff] dark:bg-[#1e1530] text-[#7c3aed]'
                      : 'bg-[#1a1a1a] dark:bg-[#f0f0ef] text-white dark:text-[#1a1a1a]'
                  }`}>
                    {msg.role === 'assistant' ? 'S' : 'U'}
                  </div>
                  {/* Bubble */}
                  <div className={`max-w-[300px] px-3 py-2 text-[13px] leading-[1.55] ${
                    msg.role === 'assistant'
                      ? 'bg-[#f5f5f4] dark:bg-[#252525] text-[#1a1a1a] dark:text-[#f0f0ef] rounded-[2px_10px_10px_10px]'
                      : 'bg-[#1a1a1a] dark:bg-[#f0f0ef] text-white dark:text-[#1a1a1a] rounded-[10px_2px_10px_10px]'
                  }`}>
                    {msg.content}
                  </div>
                </div>
                {/* Chips */}
                {msg.role === 'assistant' && msg.chips && msg.chips.length > 0 && !skillReady && (
                  <div className="flex flex-wrap gap-1.5 pl-[34px]">
                    {msg.chips.map((chip, ci) => (
                      <button
                        key={ci}
                        onClick={() => sendMessage(chip)}
                        disabled={typing}
                        className="px-[11px] py-[5px] border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-full text-[12px] text-[#475569] dark:text-[#aaa] bg-white dark:bg-[#1e1e1e] hover:border-[#7c3aed] hover:text-[#7c3aed] hover:bg-[#f5f0ff] dark:hover:bg-[#1e1530] cursor-pointer disabled:opacity-50 transition-colors"
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Typing indicator */}
            {typing && (
              <div className="flex gap-2 items-end">
                <div className="w-[26px] h-[26px] rounded-full bg-[#f5f0ff] dark:bg-[#1e1530] flex items-center justify-center text-[11px] font-semibold text-[#7c3aed] flex-shrink-0">S</div>
                <div className="bg-[#f5f5f4] dark:bg-[#252525] rounded-[2px_10px_10px_10px] px-3 py-2.5 flex gap-[3px] items-center">
                  {[0, 200, 400].map(delay => (
                    <div key={delay} className="w-[5px] h-[5px] rounded-full bg-[#64748b] animate-bounce" style={{ animationDelay: `${delay}ms` }} />
                  ))}
                </div>
              </div>
            )}

            {/* Generating state */}
            {skillReady && creatingSkill && (
              <div className="flex items-center gap-2 text-[12px] text-[#7c3aed] py-1 pl-[34px]">
                <div className="w-3 h-3 border-[1.5px] border-[#e5d5ff] border-t-[#7c3aed] rounded-full animate-spin" />
                Saving skill to your library…
              </div>
            )}

            {/* Open in editor button — shown after skill is created */}
            {skillReady && !creatingSkill && skillSlug && (
              <div className="pl-[34px] py-2">
                <button
                  onClick={() => navigate(`/skills/${skillSlug}`)}
                  className="px-4 py-2 rounded-[8px] text-[13px] font-semibold text-white cursor-pointer border-none flex items-center gap-2 hover:-translate-y-px transition-all duration-150"
                  style={{ background: 'var(--brand, #7c3aed)', fontFamily: 'inherit' }}
                >
                  Open in editor →
                </button>
              </div>
            )}

            {/* Messages remaining indicator */}
            {showRemaining && !skillReady && (
              <div className="text-[11px] text-[#64748b] dark:text-[#555] text-center py-1">
                {messagesRemaining} message{messagesRemaining !== 1 ? 's' : ''} remaining
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Compact progress indicator */}
          <div className="px-4 py-2 border-t border-[#f0f0ef] dark:border-[#2e2e2e] flex-shrink-0" style={{ borderTopColor: 'var(--border)' }}>
            {(() => {
              const stepIdx = STEPS.findIndex(s => s.key === currentStep)
              const stepNum = skillReady ? 5 : stepIdx + 1
              const stepLabel = skillReady ? 'Done' : STEPS[stepIdx]?.label || 'Purpose'
              const pct = skillReady ? 100 : Math.round(((stepNum) / STEPS.length) * 100)
              return (
                <>
                  <div className="text-[11px] font-medium mb-1.5" style={{ color: 'var(--text2)' }}>
                    <span style={{ color: 'var(--brand-light, #a78bfa)' }}>Step {stepNum} of {STEPS.length}</span>
                    <span style={{ color: 'var(--text3)' }}> · </span>
                    <span style={{ color: 'var(--text1)' }}>{stepLabel}</span>
                  </div>
                  <div className="h-[3px] rounded-[2px] overflow-hidden" style={{ background: 'var(--border, #e5e5e3)' }}>
                    <div className="h-full rounded-[2px] transition-[width] duration-400" style={{ background: 'var(--brand, #7c3aed)', width: `${pct}%` }} />
                  </div>
                </>
              )
            })()}
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t border-[#e5e5e3] dark:border-[#2e2e2e] flex gap-2 items-end flex-shrink-0">
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              disabled={typing || skillReady}
              rows={1}
              placeholder={skillReady ? 'Open in editor to refine →' : 'Type your answer…'}
              className="flex-1 px-3 py-2 border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[8px] text-[13px] font-[inherit] text-[#1a1a1a] dark:text-[#f0f0ef] dark:bg-[#222] resize-none outline-none leading-[1.5] max-h-[100px] placeholder:text-[#94a3b8] dark:placeholder:text-[#555] focus:border-[#bbb] disabled:opacity-50"
              style={{ fontFamily: 'inherit' }}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || typing || skillReady}
              className="w-[34px] h-[34px] rounded-[7px] bg-[#1a1a1a] dark:bg-[#f0f0ef] border-none cursor-pointer flex items-center justify-center text-white dark:text-[#1a1a1a] text-[14px] flex-shrink-0 hover:bg-[#333] dark:hover:bg-[#ddd] disabled:opacity-30 disabled:cursor-default"
            >
              ↑
            </button>
          </div>
        </div>

        {/* ── Right: Preview ── */}
        <div className="flex-1 flex flex-col overflow-hidden">

          {/* Preview header */}
          <div className="px-4 py-2.5 border-b border-[#e5e5e3] dark:border-[#2e2e2e] bg-white dark:bg-[#1e1e1e] flex items-center gap-2.5">
            <span className="text-[11px] font-medium text-[#64748b] dark:text-[#666] uppercase tracking-[0.6px]">Skill preview</span>
            {meta && (
              <div className="flex gap-1.5 ml-2">
                <span className="text-[10px] font-medium px-[7px] py-[2px] rounded-[3px] bg-[#f5f5f4] dark:bg-[#252525] text-[#64748b] dark:text-[#666]">✏ Draft</span>
                <span className="text-[10px] font-medium px-[7px] py-[2px] rounded-[3px] bg-[#f5f0ff] dark:bg-[#1e1530] text-[#7c3aed]">{meta.llm || 'claude'}</span>
              </div>
            )}
            <div className="ml-auto">
              {skillContent && (
                <button
                  onClick={copyMarkdown}
                  className="px-3 py-1.5 border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[6px] text-[12px] text-[#475569] dark:text-[#aaa] bg-transparent hover:bg-[#f5f5f4] dark:hover:bg-[#252525] cursor-pointer"
                >
                  {copied ? '✓ Copied' : 'Copy markdown'}
                </button>
              )}
            </div>
          </div>

          {/* Preview body */}
          <div className="flex-1 overflow-y-auto flex flex-col">
            {!skillContent ? (
              /* Empty state */
              <div className="flex-1 flex flex-col items-center justify-center gap-2.5 text-[#94a3b8] dark:text-[#444]">
                <div className="text-[32px] opacity-40">◈</div>
                <div className="text-[13px]">Your skill will appear here</div>
                <div className="text-[11px]">Answer the questions on the left to generate</div>
              </div>
            ) : (
              /* Rendered markdown */
              <div className="flex-1 px-7 py-6 prose prose-sm max-w-none
                prose-headings:font-medium prose-headings:text-[#1a1a1a] dark:prose-headings:text-[#f0f0ef]
                prose-h1:text-[20px] prose-h1:pb-2.5 prose-h1:border-b prose-h1:border-[#e5e5e3] prose-h1:mb-3.5
                prose-h2:text-[15px] prose-h2:mt-[18px] prose-h2:mb-2
                prose-h3:text-[13px] prose-h3:mt-3 prose-h3:mb-1.5
                prose-p:text-[#333] dark:prose-p:text-[#bbb] prose-p:text-[13.5px] prose-p:leading-[1.75] prose-p:mb-2.5
                prose-li:text-[#333] dark:prose-li:text-[#bbb] prose-li:text-[13.5px] prose-li:mb-1
                prose-code:text-[11.5px] prose-code:bg-[#f5f5f4] dark:prose-code:bg-[#252525] prose-code:text-[#c0392b] prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                prose-strong:font-medium
                prose-hr:border-[#e5e5e3] dark:prose-hr:border-[#2e2e2e]
                dark:text-[#f0f0ef]">
                <ReactMarkdown>{stripFrontmatter(skillContent)}</ReactMarkdown>
              </div>
            )}
          </div>

          {/* Metadata footer */}
          {meta && (
            <div className="px-4 py-2.5 border-t border-[#e5e5e3] dark:border-[#2e2e2e] bg-[#fafaf9] dark:bg-[#181818] flex gap-4 flex-wrap">
              {[
                { key: 'Name',    val: meta.name },
                { key: 'Version', val: meta.version || 'v1.0' },
                { key: 'Status',  val: '✏ Draft' },
                { key: 'Owner',   val: meta.owner || '—' },
                { key: 'Model',   val: meta.llm || 'claude' },
                { key: 'Tags',    val: meta.tags || '—' },
              ].map(({ key, val }) => (
                <div key={key} className="flex flex-col gap-0.5">
                  <div className="text-[10px] font-medium text-[#64748b] dark:text-[#555] uppercase tracking-[0.5px]">{key}</div>
                  <div className="text-[12px] text-[#475569] dark:text-[#aaa]">{val}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
